import { useEffect, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ChevronUp } from "lucide-react";
import { CN, DE, ES, FR, GB, IT, MA, NL, PL, PT, RU, TR } from "country-flag-icons/react/3x2";

interface TranslateElementConstructor {
  new (options: { pageLanguage: string; includedLanguages: string; autoDisplay: boolean }, elementId: string): unknown;
}

interface GoogleTranslateWindow extends Window {
  googleTranslateElementInit?: () => void;
  google?: {
    translate?: {
      TranslateElement?: TranslateElementConstructor;
    };
  };
}

type FlagComponent = typeof NL;

interface Language {
  code: string;
  label: string;
  Flag: FlagComponent;
}

const flagClassName = "h-4 w-6 rounded-[2px] shadow-sm shrink-0";
const LANGUAGE_STORAGE_KEY = "preferred-language";

const languages: Language[] = [
  { code: "nl", label: "Nederlands", Flag: NL },
  { code: "en", label: "English", Flag: GB },
  { code: "de", label: "Deutsch", Flag: DE },
  { code: "fr", label: "Français", Flag: FR },
  { code: "es", label: "Español", Flag: ES },
  { code: "it", label: "Italiano", Flag: IT },
  { code: "pl", label: "Polski", Flag: PL },
  { code: "tr", label: "Türkçe", Flag: TR },
  { code: "ar", label: "العربية", Flag: MA },
  { code: "zh-CN", label: "中文", Flag: CN },
  { code: "pt", label: "Português", Flag: PT },
  { code: "ru", label: "Русский", Flag: RU },
];

const setTranslateCookie = (languageCode: string) => {
  const hostname = window.location.hostname;
  const domainParts = hostname.split(".");
  const cookieDomains = [hostname, `.${hostname}`];

  for (let i = 1; i < domainParts.length; i++) {
    cookieDomains.push(`.${domainParts.slice(i).join(".")}`);
  }

  const uniqueDomains = Array.from(new Set(cookieDomains));

  for (const domain of uniqueDomains) {
    document.cookie = `googtrans=/nl/${languageCode}; path=/; domain=${domain}`;
  }

  document.cookie = `googtrans=/nl/${languageCode}; path=/`;
};

const clearTranslateCookie = () => {
  const hostname = window.location.hostname;
  const domainParts = hostname.split(".");
  const cookieDomains = [hostname, `.${hostname}`];

  for (let i = 1; i < domainParts.length; i++) {
    cookieDomains.push(`.${domainParts.slice(i).join(".")}`);
  }

  const uniqueDomains = Array.from(new Set(cookieDomains));

  for (const domain of uniqueDomains) {
    document.cookie = `googtrans=; path=/; domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }

  document.cookie = "googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
};

const getSavedLanguage = (): Language | null => {
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved) {
      return languages.find((language) => language.code === saved) ?? null;
    }
  } catch {
    // localStorage might be blocked
  }

  return null;
};

const saveLanguage = (code: string) => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    // localStorage might be blocked
  }
};

const waitForTranslateCombo = (maxAttempts = 40, delay = 100): Promise<HTMLSelectElement | null> => {
  return new Promise((resolve) => {
    let attempts = 0;

    const check = () => {
      const combo = document.querySelector(".goog-te-combo") as HTMLSelectElement | null;
      if (combo) {
        resolve(combo);
        return;
      }

      attempts += 1;
      if (attempts >= maxAttempts) {
        resolve(null);
        return;
      }

      window.setTimeout(check, delay);
    };

    check();
  });
};

export function LanguageSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState<Language>(languages[0]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const translateWindow = window as GoogleTranslateWindow;

    translateWindow.googleTranslateElementInit = () => {
      const TranslateElement = translateWindow.google?.translate?.TranslateElement;
      if (!TranslateElement) return;

      const container = document.getElementById("google_translate_element");
      if (!container) return;
      if (container.childElementCount > 0) {
        setIsInitialized(true);
        return;
      }

      new TranslateElement(
        {
          pageLanguage: "nl",
          includedLanguages: languages.map((language) => language.code).join(","),
          autoDisplay: false,
        },
        "google_translate_element",
      );

      setIsInitialized(true);
    };

    const existingScript = document.getElementById("google-translate-script") as HTMLScriptElement | null;
    if (existingScript) {
      if (translateWindow.google?.translate?.TranslateElement) {
        translateWindow.googleTranslateElementInit();
      } else {
        existingScript.addEventListener("load", translateWindow.googleTranslateElementInit, { once: true });
      }

      return () => {
        existingScript.removeEventListener("load", translateWindow.googleTranslateElementInit!);
      };
    }

    const script = document.createElement("script");
    script.id = "google-translate-script";
    script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    if (document.getElementById("gt-hide-style")) {
      return;
    }

    const style = document.createElement("style");
    style.id = "gt-hide-style";
    style.textContent = `
      #google_translate_element,
      .goog-te-banner-frame,
      .goog-te-gadget,
      .skiptranslate,
      .goog-te-spinner-pos,
      .goog-te-spinner-animation,
      .VIpgJd-ZVi9od-aZ2wEe-wOHMyf,
      .VIpgJd-ZVi9od-aZ2wEe-OiiCO,
      .VIpgJd-ZVi9od-aZ2wEe,
      .VIpgJd-ZVi9od-aZ2wEe-Jt5cK,
      [class*="VIpgJd-ZVi9od-aZ2wEe-wOHMyf"],
      [class*="VIpgJd-ZVi9od-aZ2wEe-OiiCO"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        height: 0 !important;
        width: 0 !important;
        overflow: hidden !important;
        position: absolute !important;
        pointer-events: none !important;
      }
      body {
        top: 0 !important;
        position: static !important;
      }
      .goog-text-highlight {
        background: none !important;
        box-shadow: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.getElementById("gt-hide-style")?.remove();
    };
  }, []);

  useEffect(() => {
    const savedLang = getSavedLanguage();
    if (savedLang) {
      setCurrentLanguage(savedLang);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const atTop = scrollTop <= 10;
      const atBottom = window.innerHeight + scrollTop >= document.documentElement.scrollHeight - 100;
      const isMobile = window.innerWidth < 768;
      setIsVisible(atTop || (!isMobile && atBottom));
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!isInitialized || currentLanguage.code === "nl") {
      return;
    }

    const applyLanguage = async () => {
      setTranslateCookie(currentLanguage.code);
      const combo = await waitForTranslateCombo();
      if (!combo || cancelled) return;

      if (combo.value !== currentLanguage.code) {
        combo.value = currentLanguage.code;
        combo.dispatchEvent(new Event("change", { bubbles: true }));
      }
    };

    void applyLanguage();

    return () => {
      cancelled = true;
    };
  }, [currentLanguage.code, isInitialized]);

  const handleSelectLanguage = (language: Language) => {
    setCurrentLanguage(language);
    setIsOpen(false);
    saveLanguage(language.code);

    if (language.code === "nl") {
      clearTranslateCookie();

      const combo = document.querySelector(".goog-te-combo") as HTMLSelectElement | null;
      if (combo) {
        combo.value = "";
        combo.dispatchEvent(new Event("change", { bubbles: true }));
      }

      const frame = document.querySelector(".goog-te-banner-frame") as HTMLIFrameElement | null;
      if (frame?.contentDocument) {
        const restoreBtn = frame.contentDocument.querySelector(".goog-close-link") as HTMLElement | null;
        restoreBtn?.click();
      }

      window.location.reload();
      return;
    }

    setTranslateCookie(language.code);
  };

  const CurrentFlag = currentLanguage.Flag;

  return (
    <>
      <div id="google_translate_element" aria-hidden="true" />

      <div className={`notranslate fixed bottom-5 left-1/2 z-50 -translate-x-1/2 transition-all duration-300 ${isVisible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"}`} translate="no">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-auto gap-2 rounded-full border bg-background px-3 py-2 shadow-lg shadow-black/20 transition-all duration-150 hover:scale-105"
              aria-label={`${currentLanguage.code.toUpperCase()} - Select language`}
            >
              <CurrentFlag className={flagClassName} aria-hidden="true" />
              <span className="text-sm font-medium uppercase">{currentLanguage.code.split("-")[0]}</span>
              <ChevronUp className={`h-3 w-3 text-muted-foreground transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`} />
            </Button>
          </PopoverTrigger>

          <PopoverContent
            className="notranslate max-h-80 w-48 overflow-y-auto border bg-background p-1 shadow-lg"
            align="end"
            side="top"
            sideOffset={8}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex flex-col" role="listbox" aria-label="Available languages">
              {languages.map((language) => {
                const Flag = language.Flag;

                return (
                  <button
                    key={language.code}
                    onClick={() => handleSelectLanguage(language)}
                    className={`flex items-center gap-3 rounded-md px-3 py-2 text-left transition-colors duration-150 hover:bg-accent focus:bg-accent focus:outline-none ${currentLanguage.code === language.code ? "bg-accent/50" : ""}`}
                    role="option"
                    aria-selected={currentLanguage.code === language.code}
                  >
                    <Flag className={flagClassName} aria-hidden="true" />
                    <span className="text-sm">{language.label}</span>
                  </button>
                );
              })}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );
}
